import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { Crown, Leaf, Wind, Star, CircleCheckBig, MapPin, Package, Mail, Phone } from 'lucide-react';

/* ── Fallback content ── */
const fallbackContent = {
  en: {
    premium: {
      badge: 'Flagship Collection',
      tagline: 'The pinnacle of tobacco craftsmanship',
      description:
        'Our most prestigious collection — the Royal Crown Series represents over three decades of perfection. Hand-selected leaves from the finest tobacco-growing regions are aged to peak maturity before being masterfully blended by our senior artisans.',
      features: [
        'Hand-selected and aged tobacco leaves',
        'Minimum 24-month aging process',
        'Master blender certified blend',
        'ISO 9001:2015 quality certified',
        'Available in 20 and 40 count packs',
      ],
      variants: ['Crown Regular', 'Crown Ultra Light', 'Crown Menthol', 'Crown Gold Reserve'],
      origins: ['Zimbabwe', 'Brazil', 'Turkey', 'Virginia, USA'],
    },
    classic: {
      badge: 'Heritage Line',
      tagline: 'Generations of mastery in every blend',
      description:
        'The Heritage Collection carries forward recipes passed down through five generations of master blenders. Each blend is a testament to the enduring art of traditional tobacco craftsmanship.',
      features: [
        'Five-generation family blending recipes',
        'Air-cured Virginia leaf base',
        'Traditional slow-blending methods',
        'Full-bodied, rich flavor profile',
        'Available in 10, 20, and 40 count packs',
      ],
      variants: ['Virginia Classic', 'Oriental Blend', 'Full Burley', 'Three Continents'],
      origins: ['Virginia, USA', 'Bulgaria', 'Malawi', 'Brazil'],
    },
    light: {
      badge: 'Premium Smooth',
      tagline: 'Refined subtlety for the discerning palate',
      description:
        'The Velvet Edition was developed for those who seek a smooth, refined experience without sacrificing character. Our ultra-fine filter technology combined with specially selected leaves creates a blend of unparalleled elegance.',
      features: [
        'Ultra-fine dual-layer filter technology',
        'Specially selected smooth-leaf blend',
        'Reduced tar and nicotine formulation',
        'Aroma notes: cedar, vanilla, and florals',
        'Available in slim and standard formats',
      ],
      variants: ['Velvet Blue', 'Velvet Silver', 'Velvet Menthol', 'Velvet White Slim'],
      origins: ['Greece', 'Japan', 'Canada', 'South Africa'],
    },
    special: {
      badge: 'Limited Release',
      tagline: 'Exclusive collector editions from around the world',
      description:
        "The Reserve Selection is Royal Tobacco's most exclusive offering — limited to 10,000 numbered units per release. Each edition features exotic blends sourced from rare tobacco micro-regions.",
      features: [
        'Limited to 10,000 numbered units per release',
        'Certificate of authenticity included',
        'Premium cedar-lined collector presentation box',
        'Exclusive micro-region single-origin sourcing',
        'Released once per season — strictly allocated',
      ],
      variants: ['Havana Reserve', 'Kyoto Blend', 'Marrakech Gold', 'Cape Edition'],
      origins: ['Cuba', 'Japan', 'Morocco', 'South Africa'],
    },
  },
  ar: {
    premium: {
      badge: 'المجموعة الرئيسية',
      tagline: 'قمة الحرفية في صناعة التبغ',
      description:
        'مجموعتنا الأكثر تميزاً — تمثل سلسلة التاج الملكي أكثر من ثلاثة عقود من الكمال. أوراق تبغ مختارة يدوياً من أرقى مناطق الزراعة في العالم.',
      features: [
        'أوراق تبغ مختارة يدوياً ومعتقة',
        'عملية تعتيق لمدة 24 شهراً على الأقل',
        'خلطة معتمدة من خبير المزج الرئيسي',
        'معتمدة وفق معيار ISO 9001:2015',
        'متوفرة بعبوات 20 و40 قطعة',
      ],
      variants: ['كراون ريغولار', 'كراون ألترا لايت', 'كراون منثول', 'كراون غولد ريزيرف'],
      origins: ['زيمبابوي', 'البرازيل', 'تركيا', 'فيرجينيا، الولايات المتحدة'],
    },
    classic: {
      badge: 'خط التراث',
      tagline: 'أجيال من الإتقان في كل خلطة',
      description:
        'تحمل مجموعة التراث وصفات توارثتها خمسة أجيال من خبراء المزج. كل خلطة شهادة على فن صناعة التبغ التقليدي.',
      features: [
        'وصفات عائلية متوارثة عبر خمسة أجيال',
        'قاعدة من أوراق فيرجينيا المُجففة هواءً',
        'طرق مزج تقليدية بطيئة',
        'نكهة قوية وغنية',
        'متوفرة بعبوات 10 و20 و40 قطعة',
      ],
      variants: ['فيرجينيا كلاسيك', 'أوريانتال بلند', 'فول بيرلي', 'ثري كونتيننتس'],
      origins: ['فيرجينيا، الولايات المتحدة', 'بلغاريا', 'ملاوي', 'البرازيل'],
    },
    light: {
      badge: 'ناعم فاخر',
      tagline: 'نعومة راقية لذوق الإنسان الرفيع',
      description:
        'طُوِّر إصدار المخمل لمن يبحثون عن تجربة ناعمة ومصقولة دون التضحية بالشخصية.',
      features: [
        'تقنية فلتر مزدوج فائق النعومة',
        'خلطة أوراق ناعمة مختارة خصيصاً',
        'تركيبة بنسبة قطران ونيكوتين أقل',
        'ملاحظات عطرية: أرز، فانيليا، وزهور',
        'متوفرة بأحجام سليم وستاندرد',
      ],
      variants: ['فيلفيت بلو', 'فيلفيت سيلفر', 'فيلفيت منثول', 'فيلفيت وايت سليم'],
      origins: ['اليونان', 'اليابان', 'كندا', 'جنوب أفريقيا'],
    },
    special: {
      badge: 'إصدار محدود',
      tagline: 'إصدارات حصرية للمقتنين من حول العالم',
      description:
        'التشكيلة المحدودة هي الطرح الأكثر حصرية من رويال للتبغ — محدودة بـ10,000 وحدة مرقمة لكل إصدار.',
      features: [
        'محدود بـ10,000 وحدة مرقمة لكل إصدار',
        'شهادة أصالة مرفقة مع كل علبة',
        'علبة عرض فاخرة مبطنة بخشب الأرز',
        'مصادر حصرية من مناطق إنتاج تبغ نادرة',
        'يُطرح مرة واحدة في الموسم — توزيع محدود',
      ],
      variants: ['هافانا ريزيرف', 'كيوتو بلند', 'مراكش غولد', 'كيب إيديشن'],
      origins: ['كوبا', 'اليابان', 'المغرب', 'جنوب أفريقيا'],
    },
  },
};

const icons = { premium: Crown, classic: Leaf, light: Wind, special: Star };

const parseLines = (str) =>
  (str || '').split('\n').map((s) => s.trim()).filter(Boolean);

const ProductModal = ({ data }) => {
  const { language, t } = useLanguage();
  const { isDark } = useTheme();
  const key = data?.key || 'premium';
  const fb = fallbackContent[language]?.[key] || fallbackContent.en[key] || fallbackContent.en.premium;
  const IconComponent = icons[key] || Crown;
  const isRtl = language === 'ar';
  const phoneNumber = t('contact.phone.number');
  const emailAddress = t('contact.email.address');
  const phoneHref = `tel:${phoneNumber.replace(/[^+0-9]/g, '')}`;

  const heroImage  = data?.image_url || '';
  const title      = data?.title || fb.tagline;
  const description = data?.content?.trim() ? data.content : fb.description;
  const badge      = data?.badge?.trim()   ? data.badge   : fb.badge;
  const tagline    = data?.tagline?.trim() ? data.tagline : fb.tagline;
  const features   = data?.features?.length > 0 ? data.features : fb.features;
  const variants   = data?.variants?.length > 0 ? data.variants : fb.variants;
  const origins    = data?.origins?.length  > 0 ? data.origins  : fb.origins;

  const hasVariants = variants.length > 0;
  const hasOrigins  = origins.length  > 0;

  return (
    <div className="flex flex-col">

      {/* ══════════════════════════════════════════════
          TOP HEADER — dark strip with title + badge
      ══════════════════════════════════════════════ */}
      <div
        className={`sticky top-0 z-10 px-6 py-4 flex items-center gap-3 ${
          isRtl ? 'flex-row-reverse' : ''
        } ${isDark ? 'bg-[#0A0A0A]' : 'bg-stone-950'}`}
      >
        {/* Icon */}
        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-sm bg-[var(--gold-primary)]/15 border border-[var(--gold-primary)]/25">
          <IconComponent className="w-4.5 h-4.5 text-[var(--gold-primary)]" aria-hidden="true" />
        </div>

        {/* Badge + Title */}
        <div className={`flex-1 min-w-0 ${isRtl ? 'text-right' : 'text-left'}`}>
          <span className="block text-[var(--gold-primary)] text-[10px] font-bold uppercase tracking-[0.2em] leading-none mb-1">
            {badge}
          </span>
          <h2 className="font-display text-lg sm:text-xl font-bold text-white leading-tight truncate">
            {title}
          </h2>
        </div>

        {/* Gold accent rule on the right side of header */}
        <div className={`hidden sm:block w-12 h-px flex-shrink-0 ${isRtl ? 'bg-gradient-to-l from-[var(--gold-primary)]/60 to-transparent' : 'bg-gradient-to-r from-[var(--gold-primary)]/60 to-transparent'}`} />
      </div>

      {/* Thin gold separator line under header */}
      <div className={`h-[1.5px] ${isRtl ? 'bg-gradient-to-l from-[var(--gold-primary)]/70 via-[var(--gold-primary)]/20 to-transparent' : 'bg-gradient-to-r from-[var(--gold-primary)]/70 via-[var(--gold-primary)]/20 to-transparent'}`} />

      {/* ══════════════════════════════════════════════
          BODY — image left, content right (page scrolls)
      ══════════════════════════════════════════════ */}
      <div
        className={`flex flex-col ${
          isRtl ? 'md:flex-row-reverse' : 'md:flex-row'
        }`}
      >
        {/* ── LEFT: clean product image, full and uncropped ── */}
        <div
          className={`md:w-[40%] flex items-center justify-center
            h-64 sm:h-80 md:min-h-[480px]
            ${isDark ? 'bg-[#0D0D0D]' : 'bg-stone-950'}`}
        >
          {heroImage ? (
            <div className="w-full h-full flex items-center justify-center p-6">
              <img
                src={heroImage}
                alt={title}
                className="max-w-full max-h-full object-contain drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.7))' }}
              />
            </div>
          ) : (
            /* placeholder when no image uploaded */
            <div className="flex flex-col items-center justify-center gap-3 opacity-20">
              <IconComponent className="w-16 h-16 text-[var(--gold-primary)]" />
            </div>
          )}
        </div>

        {/* Thin gold vertical separator */}
        <div
          className={`hidden md:block w-[1.5px] bg-gradient-to-b from-transparent via-[var(--gold-primary)]/40 to-transparent`}
        />

        {/* ── RIGHT: all product details ── */}
        <div
          className={`flex-1 ${
            isDark ? 'bg-[#111111]' : 'bg-white'
          }`}
        >
          <div className={`p-6 sm:p-8 ${isRtl ? 'text-right' : 'text-left'}`}>

            {/* Tagline */}
            <p className={`text-xs italic mb-5 ${isDark ? 'text-white/45' : 'text-gray-400'}`}>
              {tagline}
            </p>

            {/* Gold divider */}
            <div
              className={`h-px mb-5 ${
                isRtl
                  ? 'bg-gradient-to-l from-[var(--gold-primary)]/50 to-transparent'
                  : 'bg-gradient-to-r from-[var(--gold-primary)]/50 to-transparent'
              }`}
            />

            {/* Description */}
            <p
              className={`text-sm leading-relaxed mb-7 ${
                isDark ? 'text-white/72' : 'text-gray-600'
              }`}
            >
              {description}
            </p>

            {/* Key Features */}
            {features.length > 0 && (
              <div className="mb-7">
                <h4
                  className={`text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--gold-primary)] mb-3.5 flex items-center gap-2 ${
                    isRtl ? 'flex-row-reverse' : ''
                  }`}
                >
                  <CircleCheckBig className="w-3.5 h-3.5" aria-hidden="true" />
                  {language === 'ar' ? 'المميزات الرئيسية' : 'Key Features'}
                </h4>
                <ul className="space-y-2.5">
                  {features.map((f, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}
                    >
                      <span className="mt-[6px] flex-shrink-0 w-4 h-4 rounded-full border border-[var(--gold-primary)]/40 flex items-center justify-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold-primary)]" />
                      </span>
                      <span
                        className={`text-sm leading-snug ${
                          isDark ? 'text-white/70' : 'text-gray-600'
                        }`}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Variants + Origins */}
            {(hasVariants || hasOrigins) && (
              <div
                className={`grid gap-6 mb-7 ${
                  hasVariants && hasOrigins ? 'grid-cols-2' : 'grid-cols-1'
                }`}
              >
                {hasVariants && (
                  <div>
                    <h4
                      className={`text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--gold-primary)] mb-3 flex items-center gap-2 ${
                        isRtl ? 'flex-row-reverse' : ''
                      }`}
                    >
                      <Package className="w-3.5 h-3.5" aria-hidden="true" />
                      {language === 'ar' ? 'الأصناف المتاحة' : 'Variants'}
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {variants.map((v, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2.5 py-1 rounded-sm border ${
                            isDark
                              ? 'bg-white/[0.04] border-white/10 text-white/65'
                              : 'bg-gray-50 border-gray-200 text-gray-600'
                          }`}
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {hasOrigins && (
                  <div>
                    <h4
                      className={`text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--gold-primary)] mb-3 flex items-center gap-2 ${
                        isRtl ? 'flex-row-reverse' : ''
                      }`}
                    >
                      <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                      {language === 'ar' ? 'مناطق المنشأ' : 'Origins'}
                    </h4>
                    <ul className="space-y-1.5">
                      {origins.map((o, i) => (
                        <li
                          key={i}
                          className={`flex items-center gap-2 text-sm ${
                            isDark ? 'text-white/60' : 'text-gray-500'
                          } ${isRtl ? 'flex-row-reverse' : ''}`}
                        >
                          <span className="w-1 h-1 rounded-full bg-[var(--gold-primary)]/60 flex-shrink-0" />
                          {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* CTA */}
            <div
              className={`pt-6 border-t ${
                isDark ? 'border-white/[0.08]' : 'border-gray-100'
              } flex flex-wrap items-center gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}
            >
              <a
                href={`mailto:${emailAddress}`}
                className={`btn-shine inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--gold-primary)] text-black font-bold text-xs uppercase tracking-[0.12em] rounded-sm hover:bg-[var(--gold-hover)] transition-all ${isRtl ? 'flex-row-reverse' : ''}`}
              >
                <Mail className="w-3.5 h-3.5" aria-hidden="true" />
                {language === 'ar' ? 'طلب الكتالوج' : 'Request Catalogue'}
              </a>
              <a
                href={phoneHref}
                className={`inline-flex items-center gap-2 text-sm font-medium hover:text-[var(--gold-primary)] transition-colors ${isRtl ? 'flex-row-reverse' : ''} ${
                  isDark ? 'text-white/45' : 'text-gray-400'
                }`}
                dir="ltr"
              >
                <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                {phoneNumber}
              </a>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
